import { type RefObject, useEffect } from 'react';

/**
 * Closes an open panel on a pointer press outside it, or on Escape.
 *
 * One implementation because two panels need it — the sentence and filter menus, and the header's
 * cog — and they had grown identical copies of the same two listeners. `pointerdown` rather than
 * `click`: a press that starts outside the panel has already decided to leave it, and waiting for
 * the click lets the panel swallow the press that was meant for whatever is underneath.
 */
export const useDismissOnOutside = (
  open: boolean,
  containerRef: RefObject<HTMLElement | null>,
  onDismiss: () => void,
) => {
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) onDismiss();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, containerRef, onDismiss]);
};
